import { Alert, Form, Input, Modal, Select, Space, Tag, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useVoucherHolders } from './useVoucherHolders'
import { fetchVoucherTransfers, transferVoucherBatch, VOUCHER_REASON_MAX, type VoucherBatch } from './voucherApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface VoucherTransferModalProps {
  /** `null` — oyna yopiq */
  batch: VoucherBatch | null
  onClose: () => void
  /** `transferNo` — `null`, agar o'tkazma bajarilib, uning raqamini o'qib bo'lmagan bo'lsa */
  onTransferred: (batchNo: number, transferNo: string | null) => void
}

interface FormValues {
  targetNodeId: string
  reason?: string
}

/**
 * Partiyani OMBORGA biriktirish, AGENTGA berish yoki QAYTARISH.
 *
 * ⚠️ BITTA oyna, uch vaziyat — chunki backendda ham bitta amal
 * (`POST /api/vouchers/batches/{id}/transfer`). Nishonning turi qaysi
 * vaziyat ekanini o'zi belgilaydi, ya'ni operator "berish"mi yoki
 * "qaytarish"mi deb oldindan tanlamaydi: u faqat "endi qayerda
 * bo'ladi" degan savolga javob beradi.
 *
 * ⚠️ "Biriktirishni olib tashlash" tugmasi ATAYLAB YO'Q. Bo'sh nishon
 * `QARORLAR.md` §5.3.3 dagi nuqsonning o'zi bo'lardi — u yerda
 * maydonsiz so'rov abonentni agentidan JIMGINA uzib qo'yardi.
 * Qaytarish ham manzilli: "ombordan chiqdi, omborga qaytdi".
 *
 * ⚠️ Sabab bu yerda IXTIYORIY (`VoucherVoidModal` da majburiy). Farq
 * qaytarib bo'lishida: bekor qilish kartani o'ldiradi va "nega?"
 * savoliga javob boshqa hech qayerda qolmaydi, ko'chirish esa
 * qaytariladigan amal — teskarisini bir bosishda qilish mumkin.
 */
async function latestTransferNo(batchId: string): Promise<string | null> {
  try {
    const [latest] = await fetchVoucherTransfers({ batchId, limit: 1 })
    return latest?.transferNo ?? null
  } catch {
    return null
  }
}

export function VoucherTransferModal({ batch, onClose, onTransferred }: VoucherTransferModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const holders = useVoucherHolders(batch !== null)

  const transfer = useMutation({
    mutationFn: async (values: FormValues) => {
      const moved = await transferVoucherBatch(batch?.id ?? '', values.targetNodeId, values.reason?.trim() || undefined)
      return { batchNo: moved.batchNo, transferNo: await latestTransferNo(moved.id) }
    },
    onSuccess: ({ batchNo, transferNo }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.transfers })
      form.resetFields()
      onTransferred(batchNo, transferNo)
      onClose()
    },
  })

  const close = () => {
    form.resetFields()
    transfer.reset()
    onClose()
  }

  const currentHolder = batch?.agentId ?? batch?.nodeId ?? null

  /*
    ⚠️ Joriy ushlovchi ro'yxatdan CHIQARILADI: o'zini o'ziga berish
    hech narsani o'zgartirmaydi, lekin audit jurnaliga bo'sh yozuv
    qoldirardi va "nega ikki marta ko'chirilgan?" degan savol tug'ilardi.
  */
  /*
    ⚠️ FAOL TUGUNLAR OLDINDA, lekin NOFAOLLAR ham RO'YXATDA.

    Ro'yxatdan tur bo'yicha kesish `useVoucherHolders` da (server bilan
    AYNI shart). Bu yerda faqat TARTIB: nofaol tugunlar ko'p bo'lishi
    odatiy va aralash turganda kerakli agentni ko'z bilan qidirishga
    to'g'ri kelardi (`AgentSelect` dagi bilan bir xil qaror).
  */
  const options = holders.selectable
    .filter((n) => n.id !== currentHolder)
    .slice()
    .sort((a, b) => {
      const byState = Number(b.state === 'ACTIVE') - Number(a.state === 'ACTIVE')
      return byState !== 0 ? byState : a.displayName.localeCompare(b.displayName)
    })
    .map((node) => {
      const type = holders.types.find((k) => k.code === node.kind)
      return {
        value: node.id,
        label: `${node.displayName} (${node.code})`,
        // Qidiruv nom bo'yicha ham, kod bo'yicha ham ishlasin
        search: `${node.displayName} ${node.code}`.toLowerCase(),
        kind: type?.name ?? node.kind,
        stock: type?.holdsStock ?? false,
        state: node.state,
      }
    })

  /*
    ⚠️ NOFAOL NISHON TO'SILMAYDI, OGOHLANTIRILADI.

    Server holatga umuman qaramaydi (`VoucherRepository.holderKind`
    faqat turni o'qiydi), ya'ni tanlovni to'sish panelni serverdan
    qattiqroq qilardi — aynan shu qattiqlik agentlarni ro'yxatdan
    yo'qotgan edi.

    Lekin OQIBATI ham yashirilmaydi: yopilgan tugunga biriktirilgan
    partiya kundalik ekranlarda ko'rinmay qolishi mumkin. Bu «qilma»
    emas, «bilib qil» — sabab `useVoucherHolders` javadoc'ida.
  */
  const targetId = Form.useWatch('targetNodeId', form)
  const targetState = options.find((o) => o.value === targetId)?.state ?? null
  const targetInactive = targetState !== null && targetState !== 'ACTIVE'

  return (
    <Modal
      open={batch !== null}
      title={t('voucher.batch.transferTitle')}
      okText={t('voucher.batch.transfer')}
      cancelText={t('common.cancel')}
      confirmLoading={transfer.isPending}
      okButtonProps={{ disabled: options.length === 0 }}
      onCancel={close}
      destroyOnHidden
      width={520}
      onOk={() => submitOnValid(form, (values) => transfer.mutate(values))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('voucher.batch.transferHint')}
        </Typography.Paragraph>

        {/*
          ⚠️ "Ro'yxat bo'sh" va "ro'yxat kelmadi" — IKKI XIL holat va
          ikki xil xabar (`useVoucherHolders.reliable`). Ularni bir xil
          ko'rsatish operatorga "bizda ombor ochilmagan ekan" degan
          YOLG'ON xulosa berardi.
        */}
        {!holders.loading && holders.reason === 'no-permission' ? (
          <Alert type="warning" showIcon message={t('voucher.batch.transferNoPermission')} />
        ) : null}
        {!holders.loading && holders.reason === 'failed' ? (
          <Alert type="error" showIcon message={t('voucher.batch.transferLoadFailed')} />
        ) : null}
        {holders.reliable && options.length === 0 ? (
          <Alert type="info" showIcon message={t('voucher.batch.transferNoTargets')} />
        ) : null}

        {transfer.isError ? (
          <Alert type="error" showIcon message={translateError(transfer.error, t)} />
        ) : null}

        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={transfer.isPending}>
          <Form.Item
            name="targetNodeId"
            label={t('voucher.batch.transferTarget')}
            extra={t('voucher.batch.transferTargetHint')}
            rules={[{ required: true, message: t('voucher.batch.transferTargetRequired') }]}
          >
            <Select
              autoFocus
              showSearch
              loading={holders.loading}
              filterOption={(input, option) =>
                (option?.search ?? '').includes(input.trim().toLowerCase())
              }
              options={options}
              optionRender={(option) => (
                <Space>
                  <span>{option.data.label}</span>
                  {/* Tur NOMI bazadan keladi — kodda `WAREHOUSE` yozilmaydi */}
                  <Tag color={option.data.stock ? 'blue' : 'gold'}>{option.data.kind}</Tag>
                  {/* Holat KO'RSATILADI, tanlov TO'SILMAYDI — sabab yuqorida */}
                  {option.data.state !== 'ACTIVE' ? (
                    <Tag color="default">{t(`orgNodeState.${option.data.state}`)}</Tag>
                  ) : null}
                </Space>
              )}
            />
          </Form.Item>

          {targetInactive ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('voucher.batch.transferInactiveTarget', {
                state: t(`orgNodeState.${targetState}`),
              })}
              description={t('voucher.batch.transferInactiveHint')}
            />
          ) : null}

          <Form.Item
            name="reason"
            label={t('actions.reason')}
            extra={t('voucher.batch.transferReasonHint')}
            rules={[{ max: VOUCHER_REASON_MAX, message: t('voucher.voidReasonTooLong') }]}
          >
            <Input.TextArea rows={2} maxLength={VOUCHER_REASON_MAX} showCount />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
